import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TOAST_DURATION } from '@/lib/constants';

interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success';
  duration?: number;
}

interface Modal {
  id: string;
  isOpen: boolean;
  data?: Record<string, unknown>;
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Mobile
  mobileMenuOpen: boolean;

  // Toasts
  toasts: Toast[];

  // Modals
  modals: Record<string, Modal>;

  // Loading states
  globalLoading: boolean;
  loadingMessage: string | null;

  // Actions - Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Actions - Mobile
  toggleMobileMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;

  // Actions - Toasts
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Actions - Modals
  openModal: (id: string, data?: Record<string, unknown>) => void;
  closeModal: (id: string) => void;
  toggleModal: (id: string) => void;
  isModalOpen: (id: string) => boolean;
  getModalData: (id: string) => Record<string, unknown> | undefined;

  // Actions - Loading
  setGlobalLoading: (loading: boolean, message?: string) => void;
}

// Generate unique toast ID
const generateToastId = () =>
  `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarOpen: true,
      sidebarCollapsed: false,
      mobileMenuOpen: false,
      toasts: [],
      modals: {},
      globalLoading: false,
      loadingMessage: null,

      // Actions - Sidebar
      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleSidebarCollapsed: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Actions - Mobile
      toggleMobileMenu: () =>
        set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),

      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),

      // Actions - Toasts
      addToast: (toast) => {
        const id = generateToastId();
        const newToast: Toast = {
          ...toast,
          id,
          duration: toast.duration ?? TOAST_DURATION,
        };

        set((state) => ({
          toasts: [...state.toasts, newToast],
        }));

        // Auto-remove toast after duration
        if (newToast.duration && newToast.duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);
        }

        return id;
      },

      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((toast) => toast.id !== id),
        })),

      clearToasts: () => set({ toasts: [] }),

      // Actions - Modals
      openModal: (id, data) =>
        set((state) => ({
          modals: {
            ...state.modals,
            [id]: { id, isOpen: true, data },
          },
        })),

      closeModal: (id) =>
        set((state) => ({
          modals: {
            ...state.modals,
            [id]: { ...state.modals[id], id, isOpen: false },
          },
        })),

      toggleModal: (id) =>
        set((state) => {
          const current = state.modals[id];
          return {
            modals: {
              ...state.modals,
              [id]: { id, isOpen: !current?.isOpen, data: current?.data },
            },
          };
        }),

      isModalOpen: (id) => {
        const { modals } = get();
        return modals[id]?.isOpen ?? false;
      },

      getModalData: (id) => {
        const { modals } = get();
        return modals[id]?.data;
      },

      // Actions - Loading
      setGlobalLoading: (loading, message) =>
        set({
          globalLoading: loading,
          loadingMessage: loading ? (message ?? null) : null,
        }),
    }),
    {
      name: 'stockchef-ui',
      // Only persist sidebar preferences
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);

// Convenience hooks for common toast operations
export const useToast = () => {
  const { addToast, removeToast, clearToasts, toasts } = useUIStore();

  return {
    toasts,
    toast: (toast: Omit<Toast, 'id'>) => addToast(toast),
    success: (title: string, description?: string) =>
      addToast({ title, description, variant: 'success' }),
    error: (title: string, description?: string) =>
      addToast({ title, description, variant: 'destructive' }),
    dismiss: removeToast,
    dismissAll: clearToasts,
  };
};

// Convenience hook for modals
export const useModal = (id: string) => {
  const { openModal, closeModal, toggleModal, isModalOpen, getModalData } =
    useUIStore();

  return {
    isOpen: isModalOpen(id),
    data: getModalData(id),
    open: (data?: Record<string, unknown>) => openModal(id, data),
    close: () => closeModal(id),
    toggle: () => toggleModal(id),
  };
};
